import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import InfoTooltip from 'components/shared/InfoTooltip'
import ZoneTypeRadio from 'components/shared/RadioButtons/ZoneTypeRadio'
import { useApp } from 'store/steps'
import { useListTicketZonesByPersonQuery } from 'store/api-slice/hooks'
import { updateTicketZoneType } from 'store/ticket-slice'
import { Person, Zone } from 'store/api-slice/types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import Loader from 'components/shared/Loader'
import { useAutoScroll } from 'utils/hooks'
import { useTickets } from 'store/ticket-slice/hooks'
import { DefaultTheme } from 'styled-components'
import { setIsErrorModalOpened } from 'store/general-slice'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import Info from 'components/shared/Info'
import { isCurrentDateWithinRange } from 'helpers/dates'
import ZonesModalTrigger from './ZonesDescription/components/ZonesModalTrigger'

interface ZoneTypeProps {
    index: number
    person: Person
    boxId: string
    theme?: DefaultTheme
}

const ZoneType = ({ index, person, boxId, theme }: ZoneTypeProps) => {
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { language } = useApp()
    const { calculateScroll, animatePendingScroll, scrollIndex } =
        useAutoScroll()
    const { removeTicketFromCart, isTicketInCart } = useTickets(boxId)
    const { shouldPromoDisableTicketsBundles } = usePromotions()

    const { dates, ticketBoxes, areTicketsUpdating } = useAppSelector(
        (state) => state.ticket
    )

    const currentTicket = ticketBoxes.find((box) => box.boxId === boxId)
    const { zone: currentZone } = currentTicket || {}

    const startDate = dates.length > 0 ? dates[0] : undefined
    const endDate = dates.length > 1 ? dates[dates.length - 1] : undefined

    const { data, isFetching, isError } = useListTicketZonesByPersonQuery({
        _locale: language,
        person,
        startDate,
        endDate,
    })
    const { zones } = data || {}

    const sectionId = `tickets-${index}-zone`

    const handleSetZone = (key: Zone) => {
        if (areTicketsUpdating) return
        if (isTicketInCart) {
            removeTicketFromCart()
        }
        dispatch(updateTicketZoneType({ zone: key, boxId }))
        calculateScroll({
            trigerringSection: 'tickets',
            trigerringAction: 'modify',
            index,
            parameter: 'zone',
        })
    }

    useEffect(() => {
        if (isFetching || !zones) return
        animatePendingScroll(sectionId)
    }, [isFetching, scrollIndex])

    useEffect(() => {
        if ((!isFetching && !person) || isError) {
            dispatch(setIsErrorModalOpened(true))
        }
    }, [person, isFetching, isError])

    return (
        <Box isFullWidth={true} id={sectionId}>
            <Box direction="row" align="center" gap={8}>
                <Text variant="heading3" color="grayscale_900" theme={theme}>
                    <FormattedMessage
                        id="tickets.zones_section_caption"
                        defaultMessage="Wybierz strefy"
                    />
                </Text>
                <InfoTooltip
                    message={formatMessage({
                        id: 'upsells.tooltip.zone_type',
                        defaultMessage:
                            'Strefy Relax i Saunaria są dostępne dla osób powyżej 16 roku życia.',
                    })}
                />
            </Box>

            {/* <Box mt={[8, 12]} align="stretch" isFullWidth={true}>
                <PontoonWinch theme={theme} isFixed />
            </Box> */}

            <ZonesModalTrigger />

            <Box gap={[8, 16]} mt={[12, 16]} align="stretch" isFullWidth={true}>
                {isFetching ? (
                    <Loader />
                ) : (
                    <>
                        {zones?.map((zone) => {
                            const { key, title, highlight } = zone

                            if (!key || !title) return null

                            return (
                                <ZoneTypeRadio
                                    key={key}
                                    id="zone"
                                    isChecked={key === currentZone}
                                    label={title}
                                    onClick={() =>
                                        areTicketsUpdating
                                            ? null
                                            : handleSetZone(key)
                                    }
                                    option={key}
                                    zone={key}
                                    theme={theme}
                                    testId={`ticket-${index}-select-zones-${key}`}
                                    highlight={
                                        !shouldPromoDisableTicketsBundles
                                            ? highlight
                                            : undefined
                                    }
                                />
                            )
                        })}

                        {isCurrentDateWithinRange(
                            '2024-09-03',
                            '2024-09-20'
                        ) && (
                            <Info
                                isFullWidthContent
                                color="info"
                                hasIcon={false}
                                align="flex-start"
                            >
                                <Box isFullWidth>
                                    <Text
                                        variant="bodySmall"
                                        color="info"
                                        align="left"
                                    >
                                        <FormattedMessage
                                            id="selfcare.accomodation.additional_header"
                                            defaultMessage="Aktualne komunikaty o dostępności atrakcji w strefie Jamango: "
                                        />
                                    </Text>
                                    {isCurrentDateWithinRange(
                                        '2024-09-03',
                                        '2024-09-13'
                                    ) && (
                                        <Text
                                            variant="bodySmall"
                                            color="info"
                                            align="left"
                                        >
                                            <FormattedMessage
                                                id="selfcare.accomodation.additional_attraction_1"
                                                defaultMessage="• Brodzik obok Rzeki Przygód – nieczynny w dniach 4.09 - 13.09.2024."
                                            />
                                        </Text>
                                    )}

                                    <Text
                                        variant="bodySmall"
                                        color="info"
                                        align="left"
                                    >
                                        <FormattedMessage
                                            id="selfcare.accomodation.additional_attraction_2"
                                            defaultMessage="• Rzeka Przygód – nieczynna w dniach 16.09 - 20.09.2024."
                                        />
                                    </Text>

                                    <Text
                                        variant="bodySmall"
                                        color="info"
                                        align="left"
                                    >
                                        <FormattedMessage
                                            id="selfcare.accomodation.additional_attraction_3"
                                            defaultMessage=" • Wyciąg do pontonów – już działa i czeka na Was!"
                                        />
                                    </Text>
                                </Box>
                            </Info>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}

export default ZoneType
